import * as Sentry from '@sentry/browser'

/**
 * Determines if an error should be sent to Sentry
 * Filters out known non-issues and expected errors
 */
export function shouldReportError(
  event: Sentry.Event,
  hint: Sentry.EventHint,
): boolean {
  const error = hint.originalException

  // Filter out network errors that are expected (user offline, etc)
  if (error instanceof Error) {
    // Filter out aborted requests (user navigation, intentional cancellation)
    if (error.name === 'AbortError' || error.message.includes('aborted')) {
      return false
    }

    // Filter out Auth0 redirect errors (these are expected during login flow)
    if (error.message.includes('auth0') && error.message.includes('redirect')) {
      return false
    }

    // Filter out ResizeObserver errors (benign browser issue)
    if (error.message.includes('ResizeObserver loop limit exceeded')) {
      return false
    }

    // Filter out ResizeObserver errors with different message format
    if (
      error.message.includes(
        'ResizeObserver loop completed with undelivered notifications',
      )
    ) {
      return false
    }

    // Filter out non-error console messages incorrectly captured
    if (error.message.includes('Non-Error promise rejection captured')) {
      return false
    }
  }

  // Filter out specific HTTP status codes
  const status = event.tags?.status_code || event.extra?.status_code
  if (status) {
    // Don't report client errors that are expected
    const ignoredStatuses = [
      401, // Unauthorized (expected when token expires)
      403, // Forbidden (expected for permission issues)
    ]

    if (ignoredStatuses.includes(Number(status))) {
      return false
    }
  }

  // Filter out errors from browser extensions
  if (event.exception?.values?.[0]?.stacktrace?.frames) {
    const frames = event.exception.values[0].stacktrace.frames
    const isExtensionError = frames.some(
      (frame) =>
        frame.filename?.includes('extension://') ||
        frame.filename?.includes('chrome-extension://') ||
        frame.filename?.includes('moz-extension://'),
    )
    if (isExtensionError) {
      return false
    }
  }

  // Filter out errors from third-party scripts
  const thirdPartyDomains = [
    'googletagmanager.com',
    'google-analytics.com',
    'doubleclick.net',
    'facebook.com',
    'twitter.com',
  ]

  if (event.exception?.values?.[0]?.stacktrace?.frames) {
    const frames = event.exception.values[0].stacktrace.frames
    const isThirdPartyError = frames.some((frame) =>
      thirdPartyDomains.some((domain) => frame.filename?.includes(domain)),
    )
    if (isThirdPartyError) {
      return false
    }
  }

  return true
}
